import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { getLeavePolicyTemplates } from '../../../services/leavePolicyTemplateService';
import { formatDate } from '../../../utils/Utils';
import { useAuth } from '../../../context/AuthContext';

const statusOptions = [
  { id: 0, labelKey: 'tenant.statusLicensed' },
  { id: 1, labelKey: 'tenant.statusTrial' },
  { id: 2, labelKey: 'tenant.statusExpired' },
];

const typeOptions = [
  { id: 0, labelKey: 'tenant.typeStandalone' },
  { id: 1, labelKey: 'tenant.typeHolding' },
  { id: 2, labelKey: 'tenant.typeSubsidiary' },
];

// GET /v1/leave-policy-templates sayfalama İSTEMEZ — düz bir dizi döner
// (res.data.data), { data, totalRowCount, ... } değil. Diğer asyncSelect'ler
// gibi sunucu tarafı arama da yok; istemci tarafında ada göre süzülüyor.
// api.js GET okumalarında reddetmez — gerçek sinyal res.data.status.
const searchLeavePolicyTemplates = async (q) => {
  const res = await getLeavePolicyTemplates();
  if (res.data?.status !== true) return [];
  const items = Array.isArray(res.data.data) ? res.data.data : [];
  const needle = (q || '').toLocaleLowerCase('tr-TR');
  const filtered = needle
    ? items.filter((c) => c.name?.toLocaleLowerCase('tr-TR').includes(needle))
    : items;
  return filtered.map((c) => ({ id: c.id, name: c.name }));
};

const getLeavePolicyTemplateById = async (id) => {
  if (!id) return null;
  const res = await getLeavePolicyTemplates();
  if (res.data?.status !== true) return null;
  const items = Array.isArray(res.data.data) ? res.data.data : [];
  const found = items.find((c) => c.id === id);
  return found ? { id: found.id, name: found.name } : null;
};

const useTenantApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const { isSuperAdmin, activeTenantId } = useAuth();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/tenants?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Convert empty strings to null for nullable backend fields (DateTime?, Guid?).
  // The .NET deserializer rejects "" for these types and fails the whole body.
  const NULLABLE_FIELDS = ['expireDate', 'registrationDate', 'parentTenantId', 'defaultHolidayCalendarId', 'defaultLeavePolicyTemplateId'];
  const preparePayload = (fd) => {
    const cleaned = { ...fd };
    NULLABLE_FIELDS.forEach((k) => {
      if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null;
    });
    const tenantType = cleaned.tenantType != null ? parseInt(cleaned.tenantType) : 0;
    // Subsidiaries inherit licensing/contract fields from the parent holding —
    // strip any local values so the DB doesn't carry misleading copies.
    if (tenantType === 2) {
      cleaned.numberOfLicenses = 0;
      cleaned.status = 0;
      cleaned.expireDate = null;
    }
    return {
      ...cleaned,
      numberOfLicenses: cleaned.numberOfLicenses ? parseInt(cleaned.numberOfLicenses) : 0,
      status: cleaned.status != null ? parseInt(cleaned.status) : 0,
      tenantType,
    };
  };

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      // Holding admin: tenantType + parentTenantId aren't in their form schema —
      // force Subsidiary under the currently active holding. Backend re-validates.
      const enriched = isSuperAdmin
        ? formData
        : { ...formData, tenantType: 2, parentTenantId: activeTenantId };
      await api.post('/v1/tenants', preparePayload(enriched));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false); return false;
    }
  }, [isSuperAdmin, activeTenantId]);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/tenants/${id}`, preparePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { setFetching(false); return false; }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) await api.delete(`/v1/tenants/${id}`);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch { return false; }
  }, []);

  const getStatusLabel = (status) => {
    const opt = statusOptions.find((o) => o.id === status);
    return opt ? t(opt.labelKey) : '-';
  };

  const getTypeLabel = (type) => {
    const opt = typeOptions.find((o) => o.id === type);
    return opt ? t(opt.labelKey) : '-';
  };

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('tenant.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'email', label: t('tenant.email'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'status', label: t('tenant.status'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => getStatusLabel(v),
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('tenant.status')}</InputLabel>
              <Select value={fl[i][0] || ''} onChange={(e) => { fl[i] = e.target.value !== '' ? [e.target.value] : []; onChange(fl[i], i, col); }}>
                <MenuItem value=""><em>-</em></MenuItem>
                {statusOptions.map(o => <MenuItem key={o.id} value={o.id}>{t(o.labelKey)}</MenuItem>)}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'tenantType', label: t('tenant.type'),
      options: {
        filter: true, sort: false, filterType: 'custom',
        customBodyRender: (v) => getTypeLabel(v),
        filterOptions: {
          defaultOp: 'eq',
          display: (fl, onChange, i, col) => (
            <FormControl variant="standard" fullWidth>
              <InputLabel>{t('tenant.type')}</InputLabel>
              <Select value={fl[i][0] || ''} onChange={(e) => { fl[i] = e.target.value !== '' ? [e.target.value] : []; onChange(fl[i], i, col); }}>
                <MenuItem value=""><em>-</em></MenuItem>
                {typeOptions.map(o => <MenuItem key={o.id} value={o.id}>{t(o.labelKey)}</MenuItem>)}
              </Select>
            </FormControl>
          ),
        },
      },
    },
    {
      name: 'parentTenantName', label: t('tenant.parentTenant'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => v || '—',
      },
    },
    {
      name: 'numberOfLicenses', label: t('tenant.licenses'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'gte' },
        // Subsidiaries inherit license count from their parent holding — show '—' instead of 0.
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (row?.tenantType === 2) return '—';
          return v ?? 0;
        },
      },
    },
    {
      name: 'expireDate', label: t('tenant.expireDate'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (row?.tenantType === 2) return '—';
          return formatDate(v, i18n.language);
        },
      },
    },
    { name: 'adminCount', label: t('tenant.adminCount'), options: { filter: false, sort: false } },
    { name: 'moduleCount', label: t('tenant.moduleCount'), options: { filter: false, sort: false } },
  ], [t, i18n.language, data]);

  const objectSchema = useMemo(() => {
    const isSubsidiary = (fd) => parseInt(fd.tenantType) === 2;
    return [
      { name: 'name', label: t('tenant.name'), type: 'text', grid: 12, required: true },
      { name: 'email', label: t('tenant.email'), type: 'email', grid: 6 },
      { name: 'phoneNumber', label: t('tenant.phoneNumber'), type: 'phone', grid: 6, dialCodeField: 'areaCode' },
      { name: 'address', label: t('tenant.address'), type: 'textarea', grid: 12 },
      { name: 'taxOffice', label: t('tenant.taxOffice'), type: 'text', grid: 6 },
      { name: 'taxNumber', label: t('tenant.taxNumber'), type: 'text', grid: 6 },
      {
        name: 'tenantType', label: t('tenant.type'), type: 'select', grid: 6,
        options: typeOptions.map(o => ({ id: o.id, name: t(o.labelKey) })),
        idKey: 'id', labelKey: 'name',
        // Holding admin doesn't pick the type — it's always Subsidiary, set in create().
        hidden: !isSuperAdmin,
      },
      {
        name: 'parentTenantId', label: t('tenant.parentTenant'), type: 'asyncSelect', grid: 6,
        // Holding admin doesn't pick the parent either — auto-set to active tenant.
        hidden: (fd) => !isSuperAdmin || !isSubsidiary(fd),
        // Only Holding tenants (tenantType=1) are valid parents.
        onSearch: async (q) => {
          const params = { 'tenantType[eq]': 1, pageSize: 50 };
          if (q) params['name[like]'] = q;
          const res = await api.get('/v1/tenants', { params });
          const r = res.data?.data;
          const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map(c => ({ id: c.id, name: c.name }));
        },
        onGetById: async (id) => {
          if (!id) return null;
          const res = await api.get(`/v1/tenants/${id}`);
          const d = res.data?.data;
          return d ? { id: d.id, name: d.name } : null;
        },
      },
      // Licensing / contract fields: holding-level only AND only superAdmin
      // can edit them. Holding admins create subsidiaries (which inherit these
      // from the parent), so they never see/touch these.
      {
        name: 'numberOfLicenses', label: t('tenant.licenses'), type: 'number', grid: 4,
        hidden: (fd) => !isSuperAdmin || isSubsidiary(fd),
      },
      {
        name: 'status', label: t('tenant.status'), type: 'select', grid: 4,
        options: statusOptions.map(o => ({ id: o.id, name: t(o.labelKey) })),
        idKey: 'id', labelKey: 'name',
        hidden: (fd) => !isSuperAdmin || isSubsidiary(fd),
      },
      {
        name: 'expireDate', label: t('tenant.expireDate'), type: 'date', grid: 4,
        hidden: (fd) => !isSuperAdmin || isSubsidiary(fd),
      },
      { name: 'registrationDate', label: t('tenant.registrationDate'), type: 'date', grid: 6 },
      {
        name: 'defaultHolidayCalendarId', label: t('tenant.defaultHolidayCalendar'), type: 'asyncSelect', grid: 6,
        onSearch: async (q) => {
          const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
          const res = await api.get('/v1/holiday-calendars', { params });
          const r = res.data?.data; const items = Array.isArray(r) ? r : (r?.data || []);
          return items.map(c => ({ id: c.id, name: c.name }));
        },
        onGetById: async (id) => {
          if (!id) return null;
          const res = await api.get(`/v1/holiday-calendars/${id}`);
          const d = res.data?.data;
          return d ? { id: d.id, name: d.name } : null;
        },
      },
      {
        name: 'defaultLeavePolicyTemplateId', label: t('tenant.defaultLeavePolicyTemplate'), type: 'asyncSelect', grid: 6,
        // Bu alan yalnız bir PROVİZYON kaydıdır — TenantDataAccess.Update onu
        // bilerek yazmaz (şablonu sonradan değiştirmek mevcut politikayı yeniden
        // kopyalamaz). Düzenleme formunda gösterilirse kullanıcı değiştirip
        // kaydeder ama hiçbir şey olmaz — bu yüzden yalnız OLUŞTURMADA (fd.id
        // yokken) gösteriliyor.
        hidden: (fd) => !!fd.id,
        description: t('tenant.defaultLeavePolicyTemplateHint'),
        onSearch: searchLeavePolicyTemplates,
        onGetById: getLeavePolicyTemplateById,
      },
    ];
  }, [t, isSuperAdmin]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useTenantApi;
